import { MonthCode, monthCodeToShortMonthAndYear } from "@package/grid-utils";

import type { TAlert } from "../../data/alerts/queries";
import type { TProduct } from "../../data";
import { parseAlertLimit } from "../../data/alerts/utils";
import { rowIdToPeriod } from "../../data/formatting/helpers";
import type { TRangeSelection } from "../market-grid/useRangeSelectionChange";

export type PriceAlertSidebarEntry = {
  id: string;
  note: string;
  productName: string;
  /** Alert condition shown in the card header. */
  title: string;
  recurring: boolean;
  columnId: string;
  rowId: string;
  productId: string;
  /** Folder the curve was in when the alert fired, so the card can be dropped if access to it goes. */
  packageId?: string | null;
  /** Identifies the firing this row came from, so a re-trigger isn't mistaken for the same event. */
  triggeredAt?: string | null;
  /** When this row first entered the sidebar — lets the panel show the newest notifications first. */
  createdAt?: number;
};

/** User-facing note only — hides empty values and the legacy default placeholder. */
export const priceAlertNoteForDisplay = (note: string | null | undefined) => {
  const trimmed = note?.trim() ?? "";
  if (!trimmed || trimmed === "Alert") return "";
  return trimmed;
};

export const PRICE_ALERT_TRIGGERED_BODY = "Alert triggered";

export const priceAlertBodyForDisplay = (note: string | null | undefined) =>
  priceAlertNoteForDisplay(note) || PRICE_ALERT_TRIGGERED_BODY;

/** `CURVE NAME: FOLDER NAME` when the folder is known, otherwise just the curve name. */
export const formatAlertCurveFolderLabel = (curveName: string, folderName?: string | null) => {
  const name = curveName.trim();
  const folder = folderName?.trim();
  if (!name) return folder ?? "";
  return folder ? `${name}: ${folder}` : name;
};

export const formatManageAlertRuleTooltip = (curveName: string, period: string, folderName?: string | null) =>
  `${formatAlertCurveFolderLabel(curveName, folderName)} [${period}]`;

export function getAlertDetails(alert: TAlert, colName: string, limitName?: string | null, folderName?: string | null) {
  const parsed = parseAlertLimit(alert.limit);
  const period = rowIdToPeriod(alert.row_id) || alert.row_id;
  const operator = alert.value_below_limit ? " >= " : " <= ";

  const limitDetails =
    parsed.columnId && parsed.rowId
      ? {
          period: MonthCode.is(parsed.rowId) ? monthCodeToShortMonthAndYear(parsed.rowId) : parsed.rowId,
        }
      : null;

  const curveLabel = formatAlertCurveFolderLabel(colName, folderName);
  return `${curveLabel} [${period}] ${operator} ${limitDetails ? `${limitName} [${limitDetails.period}]` : alert.limit}`;
}

export const buildPriceAlertEntry = (
  alert: TAlert,
  products: Record<string, TProduct> | undefined,
): PriceAlertSidebarEntry => {
  const product = products?.[alert.product_id];
  const parsed = parseAlertLimit(alert.limit);
  const limitName = parsed.productId ? products?.[parsed.productId]?.name : undefined;
  const productName = product?.name ?? "";
  const title = getAlertDetails(alert, productName, limitName, product?.packageByPackage?.name);

  return {
    id: alert.id,
    note: priceAlertNoteForDisplay(alert.note),
    productName,
    title,
    recurring: !!alert.recurring,
    columnId: alert.column_id,
    rowId: alert.row_id,
    productId: alert.product_id,
    packageId: product?.package != null ? String(product.package) : null,
    triggeredAt: alert.triggered_at ?? null,
  };
};

/** Identifies one firing of one alert, so a re-trigger is never mistaken for the event before it. */
export const priceAlertFiringKey = (entry: Pick<PriceAlertSidebarEntry, "id" | "triggeredAt">) =>
  `${entry.id}:${entry.triggeredAt ?? ""}`;

/** Enough to cover anything still on screen; the oldest are forgotten first. */
const HANDLED_FIRINGS_KEPT = 50;

/**
 * Records firings the user has read or repeated. Kept with the sidebar state so the record outlives
 * a reload and is shared by every surface, whatever puts a card back on the stored list.
 */
export const rememberHandledPriceAlertFirings = (
  handled: readonly string[],
  entries: readonly Pick<PriceAlertSidebarEntry, "id" | "triggeredAt">[],
): string[] => {
  const keys = [...new Set(entries.map(priceAlertFiringKey))];
  if (keys.length === 0) return [...handled];
  const kept = [...handled.filter((key) => !keys.includes(key)), ...keys];
  return kept.slice(Math.max(0, kept.length - HANDLED_FIRINGS_KEPT));
};

export const priceAlertsToRetireOnToastDismiss = (alerts: PriceAlertSidebarEntry[]) =>
  alerts.length === 1 ? alerts : [];

/** A firing the user has dealt with is over: its card doesn't come back, only the next firing does. */
export const dropHandledPriceAlertFirings = (
  entries: PriceAlertSidebarEntry[],
  handled: readonly string[],
): PriceAlertSidebarEntry[] => {
  if (handled.length === 0) return entries;
  const spent = new Set(handled);
  return entries.filter((entry) => !spent.has(priceAlertFiringKey(entry)));
};

/** Losing access to a folder hides its curves, so alerts naming them are dropped, not relabelled. */
export const dropPriceAlertsForFolders = (
  entries: PriceAlertSidebarEntry[],
  lostPackageIds: Iterable<string>,
): PriceAlertSidebarEntry[] => {
  const lost = new Set(lostPackageIds);
  if (lost.size === 0) return entries;
  return entries.filter((entry) => !(entry.packageId && lost.has(entry.packageId)));
};

export const priceAlertManageSelection = (alert: PriceAlertSidebarEntry): TRangeSelection => {
  const period = rowIdToPeriod(alert.rowId) || alert.rowId;

  return [
    [
      {
        productId: alert.productId,
        columnId: alert.columnId,
        rowId: period,
        rowIndex: 0,
        headerName: alert.productName,
        period,
        cellValue: undefined,
      },
    ],
  ];
};

export const mergePriceAlertRows = (
  existing: PriceAlertSidebarEntry[],
  incoming: PriceAlertSidebarEntry[],
  now = Date.now(),
): PriceAlertSidebarEntry[] => {
  const merged = new Map(existing.map((entry) => [entry.id, entry]));
  for (const entry of incoming) {
    const prev = merged.get(entry.id);
    if (!prev) {
      merged.set(entry.id, { ...entry, createdAt: entry.createdAt ?? now });
      continue;
    }
    // A firing we haven't seen before is new news and goes back to the top; re-emissions of the one
    // already on the list keep their place.
    const isNewFiring = Boolean(entry.triggeredAt) && entry.triggeredAt !== prev.triggeredAt;
    merged.set(entry.id, {
      ...prev,
      ...entry,
      packageId: entry.packageId ?? prev.packageId,
      createdAt: isNewFiring ? now : (prev.createdAt ?? now),
    });
  }
  return [...merged.values()];
};

/** Backfill entries persisted before recurring/cell fields were added. */
export const normalizePriceAlertEntry = (
  entry: Partial<PriceAlertSidebarEntry> & Pick<PriceAlertSidebarEntry, "id">,
): PriceAlertSidebarEntry => ({
  id: entry.id,
  note: priceAlertNoteForDisplay(entry.note),
  productName: entry.productName ?? "",
  title: entry.title ?? "",
  recurring: entry.recurring ?? false,
  columnId: entry.columnId ?? "",
  rowId: entry.rowId ?? "",
  productId: entry.productId ?? "",
  packageId: entry.packageId ?? null,
  triggeredAt: entry.triggeredAt ?? null,
  createdAt: entry.createdAt,
});

export const formatPriceAlertSummary = (entries: PriceAlertSidebarEntry[]) => {
  const names = [...new Set(entries.map((entry) => entry.productName).filter(Boolean))];
  if (names.length === 0) return "";
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]}, ${names[1]}`;
  const others = names.length - 2;
  return `${names[0]}, ${names[1]} and ${others} other${others !== 1 ? "s" : ""}`;
};
